'use client';

import { cn, getBackgroundClass } from '@workspace/lib';
import { AppImage, Typography } from '@workspace/ui/common';
import { sanityImageSrc } from '@/sanity/lib/image';
import { handleImageClick, getImageDimensions } from './utils';

interface ImageGridBlockData {
  _key: string;
  _type: 'imageGrid';
  isActive?: boolean;
  backgroundColor?: {
    hex: string;
    alpha?: number;
    hsl?: { h: number; s: number; l: number; a?: number };
    hsv?: { h: number; s: number; v: number; a?: number };
    rgb?: { r: number; g: number; b: number; a?: number };
  };
  images: {
    _key: string;
    image: any;
    alt: string;
    caption?: string;
    link?: string;
    openInNewTab?: boolean;
  }[];
}

interface ImageGridProps {
  block: ImageGridBlockData;
  index: number;
}

export function ImageGrid({ block }: ImageGridProps) {
  const { isActive = true, backgroundColor = { hex: '#ffffff' }, images = [] } = block;

  if (!isActive) return null;
  if (!images?.length) return null;

  const validImages = images.filter((image) => image?.image);
  if (!validImages.length) {
    return null;
  }

  const imageDimensions = getImageDimensions(validImages.length);
  const backgroundClass = getBackgroundClass(backgroundColor);

  return (
    <div className={cn('image-grid-color', backgroundClass)}>
      <section className="porta-container px-md py-md md:px-full-banner">
        <div className="">
          {/* Image Grid */}
          <div
            className={cn(
              'flex gap-4 md:gap-lg justify-between',
              validImages.length === 1 ? 'justify-center' : 'flex-wrap',
            )}
          >
            {validImages?.map((image, index) => {
              if (!image?.image) {
                console.warn(`Image at index ${index} is missing image data:`, image);
                return null;
              }

              return (
                <div
                  key={image._key || index}
                  className={cn(
                    'group relative overflow-hidden',
                    image.link && 'cursor-pointer transition-transform',
                    validImages.length === 1
                      ? 'w-242.5 md:h-186.75'
                      : 'w-full h-81.75 md:h-109.5 md:w-[calc(50%-16px)] ',
                  )}
                  onClick={() => handleImageClick(image)}
                >
                  <div className={cn('w-full h-81.75 md:h-full overflow-hidden')}>
                    <AppImage
                      src={sanityImageSrc(image.image, imageDimensions)}
                      alt={image.image?.bynderImage?.alt || image.image?.alt || image.alt || 'Image'}
                      width={imageDimensions.width}
                      height={imageDimensions.height}
                      className={cn(
                        'w-full h-81.75 object-cover transition-transform duration-300',
                        validImages.length === 1 ? 'md:h-186.75' : 'md:h-109.5',
                      )}
                    />
                  </div>

                  {/* Caption overlay */}
                  {image.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-3 transform translate-y-full transition-transform duration-300 group-hover:translate-y-0">
                      <Typography variant="bodySm" className="text-white">
                        {image.caption}
                      </Typography>
                    </div>
                  )}

                  {/* Link indicator */}
                  {image.link && (
                    <div className="absolute top-3 right-3 bg-white bg-opacity-90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <svg
                        className="w-4 h-4 text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
